import { Component, computed, inject, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { finalize, map, Observable, of, switchMap } from 'rxjs';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { TreeTableNodeCollapseEvent, TreeTableNodeExpandEvent } from 'primeng/treetable';
import _ from 'lodash';
import { EnumValues } from '@card-software/library';
import { ModalService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { AuthService } from '@card-software/nexus-utilities';
import { TransferUnit } from '@app/models/inventory/transfer-unit';
import { InventoryTransaction } from '@app/models/knitting/kntiting-requistion-report';
import { Roll } from '@app/models/knitting/roll';
import { Fabric, WorkOrder, WorkOrderSearchCommand } from '@app/models/order/work-order';
import { ProductionCardStatus } from '@app/models/production/production-card';
import { ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import {
  AggregatedRollData,
  MoveToBatchWorkOrderView,
  TransferUnitRollMap,
  TransferUnitTreeNode,
} from '@app/models/production/production-card/production-card-transfer';
import { WorkOrderType } from '@app/models/production/production-dashboard';
import { BatchingRoles } from '@app/models/roles/roles-group';
import { ProductionCardRoles } from '@app/models/roles/roles-group';
import { TreeSelectionKeys } from '@app/models/shared/tree-selection';
import { TransferUnitProxyService } from '@app/proxies/inventory/transfer-unit.proxy.service';
import { FabricProxyService } from '@app/proxies/item/fabric.proxy.service';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { UserAuthenticationService } from '@app/services/user-authentication.service';
import { KnittingUtil } from '@app/utilities/knitting-util';
import { lbsToKg } from '@app/utilities/weight.util';
import { AddFromGreigeModalComponent, AddFromGreigeModalResult } from '../_modals/add-from-greige-modal/add-from-greige-modal.component';
import { ReturnRollsToGreigeModalComponent } from '../_modals/return-rolls-to-greige-modal/return-rolls-to-greige-modal.component';

@Component({
  selector: 'app-production-card-move-to-batch',
  templateUrl: './production-card-move-to-batch.component.html',
  styleUrl: './production-card-move-to-batch.component.scss',
})
export class ProductionCardMoveToBatchComponent {
  private readonly individualProductionCardService = inject(IndividualProductionCardService);
  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly enumService = inject(EnumService);
  private readonly workOrderProxyService = inject(WorkOrderProxyService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly transferUnitProxyService = inject(TransferUnitProxyService);
  private readonly fabricProxyService = inject(FabricProxyService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly modalService = inject(ModalService);
  private readonly userAuthenticationService = inject(UserAuthenticationService);

  readonly SPLIT_CHILDREN_TU_KEY = 'Split Children Rolls';
  private readonly UNASSIGNED_TU_KEY = 'Unassigned';
  readonly WorkOrderView = MoveToBatchWorkOrderView;

  activeInventoryTransactions: InventoryTransaction[] = [];
  transferUnitRolls = signal<TransferUnitRollMap>({});
  fabricFormValues: EnumValues;

  readonly = signal<boolean>(false);
  isLoading = signal<number>(0);
  showSelectedItemsTable = signal<boolean>(false);

  otherWorkOrderRolls = signal<Roll[]>([]);
  otherWorkOrderTransferUnits = signal<TransferUnit[]>([]);
  selectedOtherWorkOrder = signal<WorkOrder | null>(null);

  /** Fabrics not present in the current work order. */
  private readonly fallbackFabrics = signal<Fabric[]>([]);

  readonly showOnlyUnbundledControl = new FormControl<boolean>(false, { nonNullable: true });
  readonly searchTermControl = new FormControl<string>('', { nonNullable: true });
  readonly selectedFabricFormControl = new FormControl<string | null>(null);
  readonly useOtherWorkOrderControl = new FormControl<boolean>(false, { nonNullable: true });
  readonly selectedFabricCodeForOtherWOControl = new FormControl<string>('', { nonNullable: true });
  readonly currentViewControl = new FormControl<MoveToBatchWorkOrderView>(MoveToBatchWorkOrderView.CurrentWorkOrder, {
    nonNullable: true,
  });

  readonly showOnlyUnbundled = toSignal(this.showOnlyUnbundledControl.valueChanges, { initialValue: false });
  readonly searchTerm = toSignal(this.searchTermControl.valueChanges, { initialValue: '' });
  readonly selectedFabricForm = toSignal(this.selectedFabricFormControl.valueChanges, { initialValue: null });
  readonly useOtherWorkOrder = toSignal(this.useOtherWorkOrderControl.valueChanges, { initialValue: false });
  readonly currentView: Signal<MoveToBatchWorkOrderView> = toSignal(this.currentViewControl.valueChanges, {
    initialValue: MoveToBatchWorkOrderView.CurrentWorkOrder,
  });

  selectionKeys = signal<TreeSelectionKeys>({});
  otherWorkOrderSelectionKeys = signal<TreeSelectionKeys>({});

  /** Held outside the tree data so selecting a roll does not collapse its group. */
  expandedRowKeys = signal<Record<string, boolean>>({});

  private previousSelectionKeys: TreeSelectionKeys = {};
  private previousOtherWorkOrderSelectionKeys: TreeSelectionKeys = {};

  /** Guards the initial selectionKeys write, which would be read back as a user selection. */
  private isInitializing = false;

  canAddFromGreige = AuthService.hasRole(ProductionCardRoles.BatchingGreigeTransferAdmin);

  readonly cols: { field: string; header: string }[] = [
    { field: 'transferUnitNumber', key: 'Production_Card_Move_To_Batch_TransferUnit', fallback: 'Transfer Unit' },
    { field: 'number', key: 'Production_Card_Move_To_Batch_RollNumber', fallback: 'Roll Number' },
    { field: 'expectedFabricCode', key: 'Production_Card_Move_To_Batch_FabricCode', fallback: 'Fabric Code' },
    { field: 'itemCode', key: 'Production_Card_Move_To_Batch_Item', fallback: 'Item' },
    { field: 'itemName', key: 'Production_Card_Move_To_Batch_ItemName', fallback: 'Item Name' },
    { field: 'fabricForm', key: 'Production_Card_Move_To_Batch_FabricForm', fallback: 'Fabric Form' },
    { field: 'knittingOrderNumber', key: 'Production_Card_Move_To_Batch_KnittingOrder', fallback: 'Knitting Order' },
    { field: 'currentWeight', key: 'Production_Card_Move_To_Batch_WeightLb', fallback: 'Weight (lb)' },
    { field: 'currentWeightKg', key: 'Production_Card_Move_To_Batch_WeightKg', fallback: 'Weight (kg)' },
    { field: 'currentWeightYards', key: 'Production_Card_Move_To_Batch_WeightYd', fallback: 'Weight (yd)' },
  ].map(({ field, key, fallback }) => ({ field, header: this.localeHelperService.translateWithFallback(key, fallback) }));

  readonly numericCols = new Set(['currentWeight', 'currentWeightKg', 'currentWeightYards']);

  readonly availableViews = [
    {
      label: this.localeHelperService.translateWithFallback('Production_Card_Move_To_Batch_CurrentWorkOrder', 'Current Work Order'),
      value: MoveToBatchWorkOrderView.CurrentWorkOrder,
    },
    {
      label: this.localeHelperService.translateWithFallback('Production_Card_Move_To_Batch_OtherWorkOrder', 'Other Work Order'),
      value: MoveToBatchWorkOrderView.OtherWorkOrder,
    },
  ];

  productionCardFabricOptions = computed(() => {
    const productionCard = this.individualProductionCardService.productionCard();
    return (productionCard?.productionCardEntries ?? []).map((entry) => ({ label: entry.fabricCode, value: entry.fabricCode }));
  });

  /** Indexed because the tree does one fabric lookup per roll per column. Work order wins over fallbacks. */
  private readonly fabricsByCode = computed<Map<string, Fabric>>(() => {
    const fabricsByCode = new Map<string, Fabric>();

    this.individualProductionCardService.workOrder?.fabrics.forEach((fabricDetail) => {
      const fabric = fabricDetail.fabric;
      if (fabric?.fabricCode) {
        fabricsByCode.set(fabric.fabricCode, fabric);
      }
    });

    this.fallbackFabrics().forEach((fabric) => {
      if (!fabricsByCode.has(fabric.fabricCode)) {
        fabricsByCode.set(fabric.fabricCode, fabric);
      }
    });

    return fabricsByCode;
  });

  private readonly readonlyTransferUnitRolls = computed<TransferUnitRollMap>(() =>
    this.buildTransferUnitRollMap(this.individualProductionCardService.availableRolls, this.individualProductionCardService.transferUnits),
  );

  private readonly activeRollsData = computed<TransferUnitRollMap>(() =>
    this.readonly() ? this.readonlyTransferUnitRolls() : this.transferUnitRolls(),
  );

  constructor() {
    this.fabricFormValues = this.enumService.getEnum('FabricForm', 'itemEnums')?.values || {};

    this.activeInventoryTransactions = this.individualProductionCardService.activeInventoryTransactionsInBatching;

    // Signal, not getProductionCardOrThrow(): a throw here kills the view instead of degrading to readonly.
    const isBatchingAdmin = this.userAuthenticationService.roles.includes(BatchingRoles.BatchingAdmin);
    const productionCard = this.individualProductionCardService.productionCard();
    if (!isBatchingAdmin || productionCard?.status !== ProductionCardStatus.PlanningForBatching) {
      this.readonly.set(true);
    }

    this.selectedFabricCodeForOtherWOControl.valueChanges.subscribe((fabricCode) => this.loadOtherWorkOrderRolls(fabricCode));

    this.getTransferUnitRolls();
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  //#region Transfer unit map
  getTransferUnitRolls() {
    const rolls = this.individualProductionCardService.availableRolls;
    const transferUnits = this.individualProductionCardService.transferUnits;
    const rollNumbersChecked = this.activeInventoryTransactions.flatMap((tx) => tx.lines || []).map((line) => line.lotItemKey.number);

    this.fetchFallbackFabrics(rolls);

    const transferUnitRollMap = this.buildTransferUnitRollMap(rolls, transferUnits);
    this.transferUnitRolls.set(transferUnitRollMap);

    this.isInitializing = true;
    const initialSelectionKeys = this.buildSelectionKeys(rollNumbersChecked, transferUnits, transferUnitRollMap);
    this.selectionKeys.set(initialSelectionKeys);
    this.previousSelectionKeys = { ...initialSelectionKeys };
    this.isInitializing = false;
  }

  private buildTransferUnitRollMap(rolls: Roll[], transferUnits: TransferUnit[]): TransferUnitRollMap {
    const transferUnitRollMap: TransferUnitRollMap = {};
    const unassignedRolls: Roll[] = [];

    rolls.forEach((roll) => {
      const transferUnit = this.findTransferUnitForRoll(roll, transferUnits);
      if (transferUnit) {
        transferUnitRollMap[transferUnit.number] ??= [];
        transferUnitRollMap[transferUnit.number].push(roll);
      } else {
        unassignedRolls.push(roll);
      }
    });

    if (unassignedRolls.length > 0) {
      transferUnitRollMap[this.UNASSIGNED_TU_KEY] = unassignedRolls;
    }

    return transferUnitRollMap;
  }

  /** Split rolls keep the transfer unit of their root roll, so highestRollNumber is what the TU lists. */
  private findTransferUnitForRoll(roll: Roll | undefined, transferUnits: TransferUnit[], rollNumber?: string): TransferUnit | undefined {
    const rollNumberForTU = roll?.highestRollNumber || roll?.number || rollNumber;
    if (!rollNumberForTU) return undefined;
    return transferUnits.find((transferUnit) => transferUnit.rollNumbers.includes(rollNumberForTU));
  }
  //#endregion

  //#region Selection state
  private buildSelectionKeys(
    rollNumbersChecked: string[],
    transferUnits: TransferUnit[],
    transferUnitRollMap: TransferUnitRollMap,
  ): TreeSelectionKeys {
    const selectionKeys: TreeSelectionKeys = {};
    const rollByNumber = new Map(
      Object.values(transferUnitRollMap)
        .flat()
        .map((roll) => [roll.number, roll]),
    );

    rollNumbersChecked.forEach((rollNumber) => {
      const transferUnit = this.findTransferUnitForRoll(rollByNumber.get(rollNumber), transferUnits, rollNumber);
      const groupKey = transferUnit?.number ?? this.UNASSIGNED_TU_KEY;

      if (!transferUnit && !transferUnitRollMap[this.UNASSIGNED_TU_KEY]?.some((roll) => roll.number === rollNumber)) {
        return;
      }

      selectionKeys[`${groupKey}-${rollNumber}`] = { checked: true };
      this.updateParentSelectionState(groupKey, rollNumbersChecked, transferUnitRollMap, selectionKeys);
    });

    return selectionKeys;
  }

  private updateParentSelectionState(
    transferUnitNumber: string,
    rollNumbersChecked: string[],
    transferUnitRollMap: TransferUnitRollMap,
    selectionKeys: TreeSelectionKeys,
  ): void {
    const rollsInTU = transferUnitRollMap[transferUnitNumber];
    if (!rollsInTU) return;

    const allRollsInTU = rollsInTU.map((roll) => roll.number!);
    const checkedRollsInTU = rollNumbersChecked.filter((rollNumber) => allRollsInTU.includes(rollNumber));

    if (checkedRollsInTU.length === allRollsInTU.length) {
      selectionKeys[transferUnitNumber] = { checked: true };
    } else if (checkedRollsInTU.length > 0) {
      selectionKeys[transferUnitNumber] = { checked: false, partialChecked: true };
    }
  }

  onSelectionChange(event: TreeSelectionKeys) {
    if (this.isInitializing) {
      return;
    }

    this.processSelectionChanges(this.previousSelectionKeys, event);
    this.selectionKeys.set(event);
    this.previousSelectionKeys = { ...event };
  }

  onOtherWorkOrderSelectionChange(event: TreeSelectionKeys) {
    this.processSelectionChanges(this.previousOtherWorkOrderSelectionKeys, event);
    this.otherWorkOrderSelectionKeys.set(event);
    this.previousOtherWorkOrderSelectionKeys = { ...event };
  }

  private processSelectionChanges(previousKeys: TreeSelectionKeys, currentKeys: TreeSelectionKeys): void {
    const allKeys = new Set([...Object.keys(currentKeys), ...Object.keys(previousKeys)]);
    const rollChanges: { rollNumber: string; isSelected: boolean }[] = [];

    allKeys.forEach((key) => {
      if (!this.isRollKey(key)) {
        return;
      }

      const wasSelected = previousKeys[key]?.checked || false;
      const isSelected = currentKeys[key]?.checked || false;

      if (wasSelected !== isSelected) {
        rollChanges.push({ rollNumber: this.extractRollNumberFromKey(key), isSelected });
      }
    });

    if (rollChanges.length === 0) {
      return;
    }

    // A single user action never mixes adds and removes, so the first change decides the direction.
    const isAdding = rollChanges[0].isSelected;
    const rollNumbers = rollChanges.map((change) => change.rollNumber);

    if (rollChanges.length === 1) {
      this.rollSelected(rollNumbers[0], isAdding);
    } else {
      this.batchRollsSelected(rollNumbers, isAdding);
    }
  }

  private isRollKey(key: string): boolean {
    return this.extractRollNumberFromKey(key).includes('R-');
  }

  private extractRollNumberFromKey(key: string): string {
    return key.substring(key.indexOf('R-'));
  }

  getSelectedItems() {
    this.showSelectedItemsTable.update((show) => !show);
  }
  //#endregion

  //#region Tree data
  bundledRollNumbers = computed<Set<string>>(() => {
    const bundles = this.individualProductionCardService.productionCardBundles;

    const active = [
      ProductionCardBundleStatus.Processing,
      ProductionCardBundleStatus.ReadyForWeighing,
      ProductionCardBundleStatus.Weighed,
      ProductionCardBundleStatus.BRD,
    ];
    const deactivated = [ProductionCardBundleStatus.Used, ProductionCardBundleStatus.Removed];

    const activeBundles = bundles.filter((bundle) => active.includes(bundle.status));
    const oldBundles = bundles.filter((bundle) => deactivated.includes(bundle.status));

    // Narrowed to active bundles only once a reprocess left deactivated ones behind; otherwise all count.
    const relevantBundles = !_.isEmpty(oldBundles) && !_.isEmpty(activeBundles) ? activeBundles : bundles;

    return new Set(relevantBundles.flatMap((bundle) => bundle.rollNumbers));
  });

  rollsAvailableForReturn = computed<Roll[]>(() => {
    const bundledRolls = this.bundledRollNumbers();
    const productionCardId = this.individualProductionCardService.productionCard()?.id;
    const rollNumbersInTransactions = new Set(
      this.individualProductionCardService
        .activeInventoryTransactionsInBatchingSignal()
        .flatMap((tx) => (tx.lines || []).map((line) => line.lotItemKey.number)),
    );

    return this.individualProductionCardService.availableRolls.filter(
      (roll) =>
        rollNumbersInTransactions.has(roll.number!) &&
        !bundledRolls.has(roll.number!) &&
        roll.currentlyLinkedToProductionCardKey?.reference === productionCardId,
    );
  });

  selectedTreeData = computed<TransferUnitTreeNode[]>(() => {
    const rollsData = this.withSplitChildrenGroup(this.activeRollsData());
    const selectedKeys = this.selectionKeys();
    const bundledRolls = this.bundledRollNumbers();
    const filterUnbundled = this.showOnlyUnbundled() && this.readonly();

    const nodes = Object.entries(rollsData).flatMap(([transferUnitNumber, rolls]) => {
      // Split children have no selection keys of their own — they are always shown as a whole group.
      let selectedRolls =
        transferUnitNumber === this.SPLIT_CHILDREN_TU_KEY
          ? rolls
          : rolls.filter((roll) => selectedKeys[`${transferUnitNumber}-${roll.number}`]?.checked);

      if (filterUnbundled) {
        selectedRolls = selectedRolls.filter((roll) => !bundledRolls.has(roll.number!));
      }

      if (selectedRolls.length === 0) {
        return [];
      }

      return [this.buildTransferUnitNode(transferUnitNumber, transferUnitNumber, selectedRolls, selectedRolls.length, rolls.length, true)];
    });

    return this.sortNodesByTransferUnitNumber(nodes);
  });

  treeData = computed<TransferUnitTreeNode[]>(() => {
    const rollsData = this.activeRollsData();
    const term = this.searchTerm().toLowerCase().trim();
    const fabricFormFilter = this.selectedFabricForm();
    const isReadonly = this.readonly();
    const selectedKeys = this.selectionKeys();

    const nodes = Object.entries(rollsData).flatMap(([transferUnitNumber, rolls]) => {
      const filteredRolls = this.applyRollFilters(transferUnitNumber, rolls, term, fabricFormFilter);
      if (filteredRolls.length === 0) {
        return [];
      }

      const checkedRolls = isReadonly
        ? filteredRolls.length
        : filteredRolls.filter((roll) => selectedKeys[`${transferUnitNumber}-${roll.number}`]?.checked).length;

      return [this.buildTransferUnitNode(transferUnitNumber, transferUnitNumber, filteredRolls, checkedRolls, filteredRolls.length, false)];
    });

    return this.sortNodesByTransferUnitNumber(nodes);
  });

  otherWorkOrderTreeData = computed<TransferUnitTreeNode[]>(() => {
    const rolls = this.otherWorkOrderRolls();
    if (rolls.length === 0) {
      return [];
    }

    const selectedKeys = this.otherWorkOrderSelectionKeys();
    const term = this.searchTerm().toLowerCase().trim();
    const fabricFormFilter = this.selectedFabricForm();
    const rollsData = this.buildTransferUnitRollMap(rolls, this.otherWorkOrderTransferUnits());

    const nodes = Object.entries(rollsData).flatMap(([transferUnitNumber, transferUnitRolls]) => {
      const filteredRolls = this.applyRollFilters(transferUnitNumber, transferUnitRolls, term, fabricFormFilter);
      if (filteredRolls.length === 0) {
        return [];
      }

      const nodeKey = `other-wo-${transferUnitNumber}`;
      const checkedRolls = filteredRolls.filter((roll) => selectedKeys[`${nodeKey}-${roll.number}`]?.checked).length;

      return [this.buildTransferUnitNode(nodeKey, transferUnitNumber, filteredRolls, checkedRolls, filteredRolls.length, true, false)];
    });

    return this.sortNodesByTransferUnitNumber(nodes);
  });

  fabricFormOptions = computed<{ label: string; value: string }[]>(() => {
    const fabricsByCode = this.fabricsByCode();
    const fabricFormKeys = new Set<string>();

    Object.values(this.activeRollsData()).forEach((rolls) => {
      rolls.forEach((roll) => {
        const fabricForm = fabricsByCode.get(roll.expectedFabricCode ?? '')?.fabricForm;
        if (fabricForm !== undefined && fabricForm !== null) {
          fabricFormKeys.add(fabricForm.toString());
        }
      });
    });

    return Array.from(fabricFormKeys)
      .filter((key) => this.fabricFormValues[Number(key)])
      .map((key) => ({ label: this.fabricFormValues[Number(key)], value: key }));
  });

  /**
   * Returns a new map: {@link activeRollsData} is a computed and mutating its value corrupts other consumers.
   * Only children still linked to this card qualify — offering re-linked ones put a roll in two bundles.
   */
  private withSplitChildrenGroup(rollsData: TransferUnitRollMap): TransferUnitRollMap {
    if (!this.readonly()) {
      return rollsData;
    }

    const productionCardId = this.individualProductionCardService.productionCard()?.id;
    const alreadyListed = new Set(Object.values(rollsData).flatMap((rolls) => rolls.map((roll) => roll.number!)));

    const childrenRolls = this.individualProductionCardService.childrenRolls.filter(
      (roll) => roll.currentlyLinkedToProductionCardKey?.reference === productionCardId && !alreadyListed.has(roll.number!),
    );

    return childrenRolls.length > 0 ? { ...rollsData, [this.SPLIT_CHILDREN_TU_KEY]: childrenRolls } : rollsData;
  }

  private applyRollFilters(transferUnitNumber: string, rolls: Roll[], term: string, fabricFormFilter: string | null): Roll[] {
    let filteredRolls = term ? this.filterRollsBySearchTerm(transferUnitNumber, rolls, term) : rolls;

    if (fabricFormFilter) {
      const fabricsByCode = this.fabricsByCode();
      filteredRolls = filteredRolls.filter(
        (roll) => fabricsByCode.get(roll.expectedFabricCode ?? '')?.fabricForm?.toString() === fabricFormFilter,
      );
    }

    return filteredRolls;
  }

  /** A TU-number hit keeps all its rolls. A term starting with a digit searches itemCode, not fabric name. */
  private filterRollsBySearchTerm(transferUnitNumber: string, rolls: Roll[], term: string): Roll[] {
    if (transferUnitNumber.toLowerCase().includes(term)) {
      return rolls;
    }

    const termStartsWithNumber = !isNaN(Number(term.charAt(0)));
    const fabricsByCode = this.fabricsByCode();

    return rolls
      .map((roll) => {
        const rollNumber = roll.number?.toLowerCase() || '';
        if (rollNumber.includes(term)) {
          return { roll, matchScore: rollNumber.startsWith(term) ? 3 : 2 };
        }

        const haystack = termStartsWithNumber
          ? roll.itemCode?.toLowerCase() || ''
          : fabricsByCode.get(roll.expectedFabricCode ?? '')?.greige.longDescription?.toLowerCase() || '';

        if (haystack.includes(term)) {
          return { roll, matchScore: haystack.startsWith(term) ? 2 : 1 };
        }

        return { roll, matchScore: 0 };
      })
      .filter((match) => match.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore)
      .map((match) => match.roll);
  }

  private buildTransferUnitNode(
    nodeKey: string,
    transferUnitNumber: string,
    rolls: Roll[],
    checkedRolls: number,
    totalCount: number,
    spaceBeforeCount: boolean,
    countInChildLabel = true,
  ): TransferUnitTreeNode {
    const countSuffix = `${spaceBeforeCount ? ' ' : ''}(${checkedRolls}/${totalCount})`;
    const aggregated = this.getAggregatedRollData(rolls);
    const childTransferUnitLabel = countInChildLabel ? transferUnitNumber + countSuffix : transferUnitNumber;

    return {
      key: nodeKey,
      expanded: this.expandedRowKeys()[nodeKey] || false,
      data: {
        transferUnitNumber: transferUnitNumber + countSuffix,
        type: 'transferUnit' as const,
        transferUnit: transferUnitNumber,
        ...aggregated,
      },
      children: rolls.map((roll) => ({
        key: `${nodeKey}-${roll.number}`,
        data: {
          number: roll.number || '',
          expectedFabricCode: roll.expectedFabricCode || '',
          itemCode: roll.itemCode || '',
          itemName: this.fabricsByCode().get(roll.expectedFabricCode ?? '')?.greige.longDescription || '',
          fabricForm: this.getFabricFormLabel(roll.expectedFabricCode),
          knittingOrderNumber: roll.knittingOrderNumber || '',
          currentWeight: this.getRollWeightLbs(roll),
          currentWeightKg: this.getRollWeightKg(roll),
          currentWeightYards: this.getRollWeightYards(roll),
          type: 'roll' as const,
          rollNumber: roll.number,
          transferUnit: childTransferUnitLabel,
        },
      })),
    };
  }

  /** Sorts by the digits in the key so TU-200 comes before TU-1725 instead of lexicographically. */
  private sortNodesByTransferUnitNumber(nodes: TransferUnitTreeNode[]): TransferUnitTreeNode[] {
    return nodes.sort((a, b) => {
      const aNum = parseInt(a.key!.replace(/\D/g, ''), 10) || 0;
      const bNum = parseInt(b.key!.replace(/\D/g, ''), 10) || 0;
      return aNum - bNum;
    });
  }

  onNodeExpand(event: TreeTableNodeExpandEvent) {
    const nodeKey = event.node.key;
    if (!nodeKey) return;
    this.expandedRowKeys.update((expanded) => ({ ...expanded, [nodeKey]: true }));
  }

  onNodeCollapse(event: TreeTableNodeCollapseEvent) {
    const nodeKey = event.node.key;
    if (!nodeKey) return;
    this.expandedRowKeys.update((expanded) => _.omit(expanded, nodeKey));
  }
  //#endregion

  //#region Roll data helpers
  private getFabricFormLabel(expectedFabricCode?: string): string {
    const fabricForm = this.fabricsByCode().get(expectedFabricCode ?? '')?.fabricForm;
    return fabricForm === undefined || fabricForm === null ? '' : this.fabricFormValues[fabricForm] || '';
  }

  private getRollWeightLbs(roll: Roll): number {
    return KnittingUtil.getCurrentWeightInLbs(roll);
  }

  private getRollWeightKg(roll: Roll): number {
    return lbsToKg(this.getRollWeightLbs(roll));
  }

  private getRollWeightYards(roll: Roll): number {
    const poundToYard = this.fabricsByCode().get(roll.expectedFabricCode ?? '')?.poundToYard;
    return poundToYard ? this.getRollWeightLbs(roll) * poundToYard : 0;
  }

  private getAggregatedRollData(rolls: Roll[]): AggregatedRollData {
    const fabricsByCode = this.fabricsByCode();
    const joinDistinct = (values: (string | undefined)[]): string => [...new Set(values.filter(Boolean))].join(', ');

    return {
      expectedFabricCode: joinDistinct(rolls.map((roll) => roll.expectedFabricCode)),
      itemCode: joinDistinct(rolls.map((roll) => roll.itemCode)),
      itemName: joinDistinct(rolls.map((roll) => fabricsByCode.get(roll.expectedFabricCode ?? '')?.greige.longDescription)),
      fabricForm: joinDistinct(rolls.map((roll) => this.getFabricFormLabel(roll.expectedFabricCode))),
      knittingOrderNumber: joinDistinct(rolls.map((roll) => roll.knittingOrderNumber)),
      currentWeight: rolls.reduce((sum, roll) => sum + this.getRollWeightLbs(roll), 0),
      currentWeightKg: rolls.reduce((sum, roll) => sum + this.getRollWeightKg(roll), 0),
      currentWeightYards: rolls.reduce((sum, roll) => sum + this.getRollWeightYards(roll), 0),
    };
  }
  //#endregion

  //#region Add / remove rolls
  public rollSelected(rollNumber: string, isSelected: boolean) {
    const productionCard = this.individualProductionCardService.getProductionCardOrThrow();
    const isFromOtherWorkOrder = this.isRollFromOtherWorkOrder(rollNumber);
    const targetFabricCode = this.resolveTargetFabricCode(isFromOtherWorkOrder);

    this.incrementLoading();
    this.productionCardProxyService
      .addRollToInventoryCandidates(productionCard.id!, rollNumber, targetFabricCode)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.revertRollSelections([rollNumber], !isSelected);
            return;
          }

          if (isSelected) {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('Production_Card_Move_To_Batch_RollAdded', 'Roll Added'),
              this.localeHelperService.translateWithFallback(
                'Production_Card_Move_To_Batch_RollAddedMessage',
                `Roll ${rollNumber} added to batching`,
                { rollNumber },
              ),
            );

            if (isFromOtherWorkOrder) {
              this.moveOtherWorkOrderRollsToAvailable([rollNumber]);
            }
          } else {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('Production_Card_Move_To_Batch_RollRemoved', 'Roll Removed'),
              this.localeHelperService.translateWithFallback(
                'Production_Card_Move_To_Batch_RollRemovedMessage',
                `Roll ${rollNumber} removed from batching`,
                { rollNumber },
              ),
            );
          }

          this.applyTransactionResponse(response.data, [rollNumber]);
        },
        error: () => {
          this.revertRollSelections([rollNumber], !isSelected);
        },
      });
  }

  public batchRollsSelected(rollNumbers: string[], isSelected: boolean) {
    const productionCard = this.individualProductionCardService.getProductionCardOrThrow();
    const otherWORollNumbers = rollNumbers.filter((rollNumber) => this.isRollFromOtherWorkOrder(rollNumber));
    const targetFabricCode = this.resolveTargetFabricCode(otherWORollNumbers.length > 0);

    this.incrementLoading();
    this.productionCardProxyService
      .batchAddRollsToInventoryCandidates(productionCard.id!, rollNumbers, isSelected, targetFabricCode)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.revertRollSelections(rollNumbers, !isSelected);
            return;
          }

          const rollCount = rollNumbers.length;
          if (isSelected) {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('Production_Card_Move_To_Batch_RollsAdded', 'Rolls Added'),
              this.localeHelperService.translateWithFallback(
                'Production_Card_Move_To_Batch_RollsAddedMessage',
                `${rollCount} rolls added to batching`,
                { rollCount },
              ),
            );

            if (otherWORollNumbers.length > 0) {
              this.moveOtherWorkOrderRollsToAvailable(otherWORollNumbers);
            }
          } else {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('Production_Card_Move_To_Batch_RollsRemoved', 'Rolls Removed'),
              this.localeHelperService.translateWithFallback(
                'Production_Card_Move_To_Batch_RollsRemovedMessage',
                `${rollCount} rolls removed from batching`,
                { rollCount },
              ),
            );
          }

          this.applyTransactionResponse(response.data, rollNumbers);
        },
        error: () => {
          this.revertRollSelections(rollNumbers, !isSelected);
        },
      });
  }

  private resolveTargetFabricCode(hasOtherWorkOrderRolls: boolean): string | undefined {
    return hasOtherWorkOrderRolls && this.useOtherWorkOrder() ? this.selectedFabricCodeForOtherWOControl.value || undefined : undefined;
  }

  private applyTransactionResponse(transaction: InventoryTransaction | null | undefined, rollNumbers: string[]): void {
    if (transaction) {
      this.individualProductionCardService.updateSingleInventoryTransaction(transaction);
    }
    this.activeInventoryTransactions = this.individualProductionCardService.activeInventoryTransactionsInBatching;
    this.recalculateParentSelectionStates(rollNumbers);
  }

  private revertRollSelections(rollNumbers: string[], revertToChecked: boolean): void {
    const selectionKeys = { ...this.selectionKeys() };

    rollNumbers.forEach((rollNumber) => {
      selectionKeys[`${this.resolveGroupKeyForRoll(rollNumber)}-${rollNumber}`] = { checked: revertToChecked };
    });

    this.commitSelectionKeys(this.recalculateParentStates(selectionKeys, rollNumbers));
  }

  private recalculateParentSelectionStates(rollNumbers: string[]): void {
    this.commitSelectionKeys(this.recalculateParentStates({ ...this.selectionKeys() }, rollNumbers));
  }

  private recalculateParentStates(selectionKeys: TreeSelectionKeys, rollNumbers: string[]): TreeSelectionKeys {
    const transferUnitRollMap = this.transferUnitRolls();
    const affectedGroups = new Set(rollNumbers.map((rollNumber) => this.resolveGroupKeyForRoll(rollNumber)));

    affectedGroups.forEach((groupKey) => {
      const rolls = transferUnitRollMap[groupKey] || [];
      const checkedCount = rolls.filter((roll) => selectionKeys[`${groupKey}-${roll.number}`]?.checked).length;

      if (checkedCount === 0) {
        delete selectionKeys[groupKey];
      } else if (checkedCount === rolls.length) {
        selectionKeys[groupKey] = { checked: true };
      } else {
        selectionKeys[groupKey] = { checked: false, partialChecked: true };
      }
    });

    return selectionKeys;
  }

  private resolveGroupKeyForRoll(rollNumber: string): string {
    const roll = this.individualProductionCardService.availableRolls.find((r) => r.number === rollNumber);
    const transferUnit = this.findTransferUnitForRoll(roll, this.individualProductionCardService.transferUnits, rollNumber);
    return transferUnit?.number ?? this.UNASSIGNED_TU_KEY;
  }

  private commitSelectionKeys(selectionKeys: TreeSelectionKeys): void {
    this.selectionKeys.set(selectionKeys);
    this.previousSelectionKeys = { ...selectionKeys };
  }
  //#endregion

  //#region Other work order
  public fetchWorkOrders = (query: string): Observable<WorkOrder[]> => {
    const command: WorkOrderSearchCommand = { orderNumber: query };

    this.incrementLoading();

    return this.workOrderProxyService.search(command, 1, 50).pipe(
      finalize(() => this.decrementLoading()),
      map((response) => (response.data?.items ?? []).filter((workOrder) => workOrder.orderType !== WorkOrderType.GreigeOnly)),
    );
  };

  onWorkOrderSelected(event: AutoCompleteSelectEvent) {
    this.selectedOtherWorkOrder.set(event.value as WorkOrder);
    // Emits, so the valueChanges subscription clears the roll and transfer unit lists.
    this.selectedFabricCodeForOtherWOControl.setValue('');
  }

  private loadOtherWorkOrderRolls(fabricCode: string): void {
    const workOrder = this.selectedOtherWorkOrder();
    if (!workOrder || !fabricCode) {
      this.otherWorkOrderRolls.set([]);
      this.otherWorkOrderTransferUnits.set([]);
      return;
    }

    const command = {
      workOrderReference: workOrder.id!,
      targetProductionCardId: this.individualProductionCardService.getProductionCardOrThrow().id!,
      targetFabricCode: fabricCode,
    };

    this.incrementLoading();
    this.rollProxyService
      .getRollsAvailableRollsOFOtherWorkOrder(command)
      .pipe(
        switchMap((response) => {
          if (!response.success || !response.data) {
            this.otherWorkOrderRolls.set([]);
            this.otherWorkOrderTransferUnits.set([]);
            return of(null);
          }

          this.otherWorkOrderRolls.set(response.data);
          this.fetchFallbackFabrics(response.data);

          const rollNumbers = [...new Set(response.data.map((roll) => roll.highestRollNumber || roll.number!))];
          return this.transferUnitProxyService.getTransferUnitsForRollNumbers(rollNumbers);
        }),
        finalize(() => this.decrementLoading()),
      )
      .subscribe({
        next: (response) => {
          this.otherWorkOrderTransferUnits.set(response?.success ? (response.data ?? []) : []);
        },
        error: () => {
          this.otherWorkOrderRolls.set([]);
          this.otherWorkOrderTransferUnits.set([]);
        },
      });
  }

  private isRollFromOtherWorkOrder(rollNumber: string): boolean {
    return this.otherWorkOrderRolls().some((roll) => roll.number === rollNumber);
  }

  private moveOtherWorkOrderRollsToAvailable(rollNumbers: string[]): void {
    const otherWORolls = this.otherWorkOrderRolls();
    const otherWOTransferUnits = this.otherWorkOrderTransferUnits();
    const rollsToMove = otherWORolls.filter((roll) => rollNumbers.includes(roll.number!));

    if (rollsToMove.length === 0) {
      return;
    }

    this.individualProductionCardService.addRollsToAvailableRolls(rollsToMove);

    const relevantTUs = otherWOTransferUnits.filter((tu) => rollNumbers.some((rollNumber) => tu.rollNumbers.includes(rollNumber)));
    if (relevantTUs.length > 0) {
      this.individualProductionCardService.addTransferUnitsToList(relevantTUs);
      this.addRollsToTransferUnitMap(rollsToMove, relevantTUs);
    } else {
      this.incrementLoading();
      this.transferUnitProxyService
        .getTransferUnitsForRollNumbers(rollNumbers)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.individualProductionCardService.addTransferUnitsToList(response.data);
              this.addRollsToTransferUnitMap(rollsToMove, response.data);
            }
          },
        });
    }

    const remainingRolls = otherWORolls.filter((roll) => !rollNumbers.includes(roll.number!));
    this.otherWorkOrderRolls.set(remainingRolls);

    const otherWOSelectionKeys = { ...this.otherWorkOrderSelectionKeys() };
    rollNumbers.forEach((rollNumber) => {
      const transferUnit = otherWOTransferUnits.find((tu) => tu.rollNumbers.includes(rollNumber));
      const nodeKey = `other-wo-${transferUnit?.number || this.UNASSIGNED_TU_KEY}`;
      delete otherWOSelectionKeys[`${nodeKey}-${rollNumber}`];

      const noRollsLeftInTU = !remainingRolls.some((roll) => transferUnit?.rollNumbers.includes(roll.number!));
      if (otherWOSelectionKeys[nodeKey] && noRollsLeftInTU) {
        delete otherWOSelectionKeys[nodeKey];
      }
    });

    this.otherWorkOrderSelectionKeys.set(otherWOSelectionKeys);
    this.previousOtherWorkOrderSelectionKeys = { ...otherWOSelectionKeys };
  }

  private fetchFallbackFabrics(rolls: Roll[]): void {
    const currentFabricCodes = new Set(
      this.individualProductionCardService.workOrder?.fabrics.map((fd) => fd.fabric?.fabricCode).filter(Boolean) || [],
    );

    const missingFabricCodes = [...new Set(rolls.map((roll) => roll.expectedFabricCode).filter(Boolean))].filter(
      (fabricCode) => !currentFabricCodes.has(fabricCode!),
    ) as string[];

    if (missingFabricCodes.length === 0) {
      return;
    }

    this.incrementLoading();
    this.fabricProxyService
      .batchFabrics(missingFabricCodes)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.fallbackFabrics.update((current) => _.unionBy(current, response.data!, 'fabricCode'));
          }
        },
      });
  }
  //#endregion

  //#region Greige transfer modals
  openReturnRollsModal(): void {
    this.modalService.open(ReturnRollsToGreigeModalComponent, { modalClass: 'modal_large' }, {}).subscribe({
      next: (result) => {
        if (result?.action !== 'save' || !result.data) {
          return;
        }

        const rollNumbers = result.data as string[];
        this.individualProductionCardService.removeRollsFromAvailableRolls(rollNumbers);
        this.removeRollsFromTransferUnitMap(rollNumbers);
        this.refreshBatchingTransactions();
        this.individualProductionCardService.proxyGetInventoryTransactionsBatchingToGreige().subscribe();
      },
    });
  }

  openReAddFromGreigeModal(): void {
    this.modalService.open(AddFromGreigeModalComponent, { modalClass: 'modal_large' }, {}).subscribe({
      next: (result) => {
        if (result?.action !== 'save' || !result.data) {
          return;
        }

        const rolls = result.data as AddFromGreigeModalResult;
        this.individualProductionCardService.addRollsToAvailableRolls(rolls);
        this.addRollsToTransferUnitMap(rolls, this.individualProductionCardService.transferUnits);
        this.refreshBatchingTransactions();
      },
    });
  }

  private refreshBatchingTransactions(): void {
    this.incrementLoading();
    this.individualProductionCardService
      .proxyGetActiveInventoryTransactionsInBatching()
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: () => {
          this.activeInventoryTransactions = this.individualProductionCardService.activeInventoryTransactionsInBatching;
          this.getTransferUnitRolls();
        },
      });
  }

  private removeRollsFromTransferUnitMap(rollNumbers: string[]): void {
    const transferUnitRollMap: TransferUnitRollMap = {};

    Object.entries(this.transferUnitRolls()).forEach(([transferUnitNumber, rolls]) => {
      const remainingRolls = rolls.filter((roll) => !rollNumbers.includes(roll.number!));
      if (remainingRolls.length > 0) {
        transferUnitRollMap[transferUnitNumber] = remainingRolls;
      }
    });

    this.transferUnitRolls.set(transferUnitRollMap);
  }

  /** Marks the rolls selected: they are already in the transaction by the time they get here. */
  private addRollsToTransferUnitMap(rolls: Roll[], transferUnits: TransferUnit[]): void {
    const transferUnitRollMap = { ...this.transferUnitRolls() };
    const selectionKeys = { ...this.selectionKeys() };

    rolls.forEach((roll) => {
      const transferUnit = this.findTransferUnitForRoll(roll, transferUnits);
      if (!transferUnit) {
        return;
      }

      transferUnitRollMap[transferUnit.number] = _.unionBy(transferUnitRollMap[transferUnit.number] ?? [], [roll], 'number');
      selectionKeys[`${transferUnit.number}-${roll.number}`] = { checked: true };

      const rollsInTU = transferUnitRollMap[transferUnit.number];
      const checkedCount = rollsInTU.filter((r) => selectionKeys[`${transferUnit.number}-${r.number}`]?.checked).length;

      if (checkedCount === rollsInTU.length) {
        selectionKeys[transferUnit.number] = { checked: true };
      } else if (checkedCount > 0) {
        selectionKeys[transferUnit.number] = { checked: false, partialChecked: true };
      }
    });

    this.transferUnitRolls.set(transferUnitRollMap);
    this.commitSelectionKeys(selectionKeys);
  }
  //#endregion
}
